import { generateAllMetaTags, generatePageTitle, truncateDescription } from 'components/seo/utils/metaGenerators';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';
import { getFrontEndUrl } from 'utils/environment-util';

interface SEOHeadProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  keywords?: string[];
  robots?: string;
  canonical?: string;
  children?: React.ReactNode;
  noindex?: boolean;
  twitterHandle?: string;
}

/**
 * Comprehensive SEO component for all pages
 */
export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  image,
  url,
  type = 'website',
  author,
  keywords,
  robots,
  canonical,
  children,
  noindex = false,
  twitterHandle = '@ordershef',
}) => {
  // Generate full URLs
  const baseUrl = getFrontEndUrl();
  const router = useRouter();
  // Avoid SSR/client markup mismatch: compute current URL only on client
  const [clientUrl, setClientUrl] = React.useState<string | undefined>(undefined);
  React.useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.location?.href) {
        setClientUrl(window.location.href);
      } else if (router.asPath) {
        setClientUrl(`${baseUrl}${router.asPath.startsWith('/') ? '' : '/'}${router.asPath}`);
      }
    } catch {
      // no-op
    }
  }, [router.asPath, baseUrl]);
  const fullUrl = url ?? clientUrl;
  const isAbsoluteUrl = (u: string) => u.startsWith('//') || /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(u);
  const fullImageUrl = image && !isAbsoluteUrl(image) ? `${baseUrl}${image.startsWith('/') ? '' : '/'}${image}` : image;
  const canonicalUrl = canonical ?? fullUrl ?? baseUrl;

  // Process title and description
  const processedTitle = generatePageTitle(title);
  const processedDescription = truncateDescription(description);

  // Generate all meta tags
  const metaTags = generateAllMetaTags({
    title: processedTitle,
    description: processedDescription,
    image: fullImageUrl,
    url: fullUrl,
    type,
    siteName: 'Shef',
    locale: 'en_US',
    twitterHandle,
    author,
    keywords,
    robots: noindex ? 'noindex, nofollow' : robots,
  });

  return (
    <Head>
      {/* Title */}
      <title>{processedTitle}</title>

      {/* Standard Meta Tags */}
      <meta name='description' content={metaTags.standard.description} />
      <meta name='viewport' content={metaTags.standard.viewport} />
      <meta name='robots' content={metaTags.standard.robots} />
      {metaTags.standard.author && <meta name='author' content={metaTags.standard.author} />}
      {metaTags.standard.keywords && <meta name='keywords' content={metaTags.standard.keywords} />}

      {/* Open Graph Meta Tags */}
      <meta property='og:title' content={metaTags.openGraph['og:title']} />
      <meta property='og:description' content={metaTags.openGraph['og:description']} />
      <meta property='og:type' content={metaTags.openGraph['og:type']} />
      <meta property='og:site_name' content={metaTags.openGraph['og:site_name']} />
      <meta property='og:locale' content={metaTags.openGraph['og:locale']} />
      {metaTags.openGraph['og:url'] && <meta property='og:url' content={metaTags.openGraph['og:url']} />}
      {metaTags.openGraph['og:image'] && (
        <>
          <meta property='og:image' content={metaTags.openGraph['og:image']} />
          <meta property='og:image:alt' content={metaTags.openGraph['og:image:alt']} />
        </>
      )}

      {/* Twitter Card Meta Tags */}
      <meta name='twitter:card' content={metaTags.twitter['twitter:card']} />
      <meta name='twitter:title' content={metaTags.twitter['twitter:title']} />
      <meta name='twitter:description' content={metaTags.twitter['twitter:description']} />
      {metaTags.twitter['twitter:site'] && <meta name='twitter:site' content={metaTags.twitter['twitter:site']} />}
      {metaTags.twitter['twitter:creator'] && (
        <meta name='twitter:creator' content={metaTags.twitter['twitter:creator']} />
      )}
      {metaTags.twitter['twitter:image'] && (
        <>
          <meta name='twitter:image' content={metaTags.twitter['twitter:image']} />
          <meta name='twitter:image:alt' content={metaTags.twitter['twitter:image:alt']} />
        </>
      )}

      {/* Canonical URL */}
      <link rel='canonical' href={canonicalUrl} />

      {/* Favicon */}
      <link rel='icon' href='/favicon.ico' />
      <link rel='icon' type='image/png' sizes='57x57' href='/favicon-57x57.png' />

      {/* Additional children elements */}
      {children}
    </Head>
  );
};
